import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';

import { SecurityService } from 'app/core/security/security.service';
import { SystemMessage } from 'app/core/system-message/system-message-service';
import { NotificationService } from 'app/notification.service';
import { TrainingStatusEnum } from 'app/core/enums/generated/TrainingStatusEnum';
import { UserJsVm } from 'app/shared/generated/Administration/Models/Users/UserJsVm';

/**
 * Full-width strip pinned above the app shell for users still in Issuance training (dev/demo only). Clicking
 * it opens a confirm dialog and submits the current user for training-completion approval. The shell
 * (app.component) decides when to render this and shifts its content down so the banner never overlaps.
 */
@Component({
    selector: 'pcg-training-banner',
    standalone: true,
    imports: [ButtonModule, DialogModule],
    templateUrl: './training-banner.component.html',
    styleUrl: './training-banner.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrainingBannerComponent {
    private readonly http = inject(HttpClient);
    private readonly toastr = inject(NotificationService);
    private readonly sec = inject(SecurityService);

    /** Emitted after the training-completion request succeeds, so the shell can hide the banner immediately. */
    readonly completed = output<void>();

    readonly isSubmitting = signal<boolean>(false);
    readonly confirmVisible = signal<boolean>(false);

    /** Open the confirm dialog. */
    onClick() {
        this.confirmVisible.set(true);
    }

    /** Mark the current user's training complete. */
    submit() {
        this.isSubmitting.set(true);
        this.http.post<SystemMessage>('api/Administration/Users/RequestTrainingCompletion', {})
            .pipe(finalize(() => this.isSubmitting.set(false)))
            .subscribe({
                next: (r) => {
                    this.toastr.showMessage(r.message ?? '', r.messageClass);
                    if (r.isSuccessful) {
                        // Keep global security state in sync so other views reflect the new status.
                        const current = this.sec.getUser();
                        if (current) {
                            const updated = { ...current, trainingStatus: TrainingStatusEnum.TrainingComplete } as UserJsVm;
                            this.sec.setSecurity(undefined!, updated);
                        }
                        this.completed.emit();
                        // Only close on success; a failed request leaves the dialog open so the user can retry.
                        this.confirmVisible.set(false);
                    }
                },
                error: () => this.toastr.showError('Something went wrong submitting your training completion. Please try again.'),
            });
    }
}
